import React, { useEffect, useState, useCallback } from 'react';
import { Layout, Menu, Avatar, Typography, Button, Dropdown, Badge, Space, List, Empty, message } from 'antd';
import {
  DashboardOutlined, TeamOutlined, QrcodeOutlined, FileDoneOutlined,
  LogoutOutlined, ShopOutlined, FileTextOutlined, ReconciliationOutlined,
  InboxOutlined, NodeIndexOutlined, BellOutlined, SettingOutlined,
  ApartmentOutlined, BookOutlined, ShopFilled, ProjectOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { notificationApi } from '../services/api';
import dayjs from 'dayjs';

const { Sider, Content, Header } = Layout;
const { Text } = Typography;

// === 菜单项（各 Agent 模块按区域划分，互不覆盖）===

// @SHARED: 基础菜单
// @AGENT_A: 产品 + 仓库 菜单
// @AGENT_B: 报价 + 合同 菜单
// @AGENT_C: 工作流 菜单（仅管理后台）
// @AGENT_D: 通知 菜单
// @AGENT_E: 项目 菜单 (P1)

const getMenuItems = (role) => {
  const items = [
    { key: '/', icon: <DashboardOutlined />, label: '数据看板' },
    { key: '/customers', icon: <TeamOutlined />, label: '客户管理' },
    { key: '/qrcode', icon: <QrcodeOutlined />, label: '扫码获客' },
    // @AGENT_B: 报价 + 合同
    { type: 'divider' },
    { key: '/quotations', icon: <FileTextOutlined />, label: '报价管理' },
    { key: '/contracts', icon: <ReconciliationOutlined />, label: '合同管理' },
    // @AGENT_A: 产品 + 仓库
    { type: 'divider' },
    { key: '/products', icon: <ShopOutlined />, label: '产品中心' },
    { key: '/suppliers', icon: <ShopFilled />, label: '供应商管理' },
    { key: '/warehouse', icon: <InboxOutlined />, label: '仓库管理' },
    // @AGENT_E: 项目看板
    { key: '/projects', icon: <ProjectOutlined />, label: '项目看板' },
  ];

  // @AGENT_C: 工作流配置 + 审计日志 (仅 admin 可见)
  if (role === 'admin') {
    items.push(
      { type: 'divider' },
      { key: '/workflow', icon: <ApartmentOutlined />, label: '工作流配置' },
      { key: '/audit-logs', icon: <SettingOutlined />, label: '审计日志' },
    );
  }

  // @AGENT_D: 技术文档 (admin+manager 可见)
  if (role === 'admin' || role === 'manager') {
    items.push(
      { type: 'divider' },
      { key: '/documents', icon: <BookOutlined />, label: '技术文档' },
    );
  }

  // @SHARED: 底部
  items.push(
    { type: 'divider' },
    { key: '/logs', icon: <FileDoneOutlined />, label: '跟进记录' },
  );

  return items;
};

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const roleLabels = { admin: '管理员', manager: '主管', sales: '销售' };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationApi.list({ read: false, pageSize: 1 });
      setUnreadCount(res.total || 0);
    } catch (e) {
      // 静默失败
    }
  }, []);

  const fetchRecentNotifications = useCallback(async () => {
    try {
      const res = await notificationApi.list({ pageSize: 5 });
      setNotifications(res.list || []);
    } catch (e) {
      // 静默失败
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // 30s 轮询
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleNotificationOpen = () => {
    fetchRecentNotifications();
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationApi.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      fetchUnreadCount();
    } catch (e) {
      message.error('操作失败');
    }
  };

  const notificationDropdown = {
    items: notifications.length === 0
      ? [{ key: 'empty', label: <Empty description="暂无通知" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '12px 0' }} />, disabled: true }]
      : [
          ...notifications.map((n) => ({
            key: n.id,
            label: (
              <div style={{ maxWidth: 300, padding: '4px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ fontSize: 13, flex: 1 }} ellipsis>{n.title}</Text>
                  {!n.read && <Badge status="processing" />}
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{n.content?.substring(0, 60)}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {dayjs(n.sentAt).format('MM-DD HH:mm')}
                  </Text>
                  {!n.read && (
                    <Button type="link" size="small" style={{ padding: 0, fontSize: 11 }}
                      onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id); }}>
                      标为已读
                    </Button>
                  )}
                </div>
              </div>
            ),
            onClick: () => {
              if (!n.read) handleMarkRead(n.id);
              if (n.link) navigate(n.link);
            },
          })),
          { type: 'divider' },
          {
            key: 'viewAll',
            label: <Text style={{ color: '#1677ff', fontSize: 13 }}>查看全部通知</Text>,
            onClick: () => navigate('/notifications'),
          },
        ],
  };

  const userMenu = {
    items: [
      { key: 'notifications', icon: <BellOutlined />, label: '通知中心' },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'notifications') navigate('/notifications');
      if (key === 'logout') { logout(); navigate('/login'); }
    },
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={220} theme="light" style={{ borderRight: '1px solid #f0f0f0' }}>
        <div style={{
          padding: '20px 16px 16px', display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, background: '#07c160',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
          }}>💼</div>
          <div>
            <div style={{ fontWeight: 500, fontSize: 15 }}>微信CRM</div>
            <Text type="secondary" style={{ fontSize: 11 }}>客户管理系统</Text>
          </div>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname === '/' ? '/' : location.pathname]}
          items={getMenuItems(user?.role)}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0, paddingTop: 8 }}
        />
      </Sider>

      <Layout>
        <Header style={{
          background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: 56,
        }}>
          <Space size={16}>
            <Dropdown menu={notificationDropdown} trigger={['click']} placement="bottomRight"
              onOpenChange={(open) => { if (open) handleNotificationOpen(); }}>
              <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 36, width: 36 }} />
              </Badge>
            </Dropdown>

            <Dropdown menu={userMenu} placement="bottomRight">
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar style={{ background: '#07c160' }}>{user?.name?.[0]}</Avatar>
                <span style={{ fontSize: 14 }}>{user?.name}</span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {roleLabels[user?.role] || user?.role}
                </Text>
              </div>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ padding: 24, background: '#f5f5f5', minHeight: 'calc(100vh - 56px)' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, minHeight: '100%' }}>
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}
